const MAX_URL_LENGTH = 2048;

/** Matches a scheme-less `www.` URL, e.g. `www.google.com/path`. */
const BARE_WWW_PREFIX = /^www\./i;

/**
 * Gives a scheme-less `www.` URL the implied `https://` scheme so it can be
 * parsed and linked as an absolute URL. Any other input is returned unchanged.
 *
 * Message text is authored by hand, so admins routinely type `www.google.com`
 * rather than the full `https://www.google.com`. Only the href needs the
 * scheme - callers keep displaying the text exactly as it was written.
 *
 * @param {string} url - A URL token taken from message text.
 * @returns {string} The URL with an explicit http(s) scheme where one was implied.
 */
export function normalizeMessageURL(url: string): string {
  if (typeof url !== 'string') {
    return url;
  }

  return BARE_WWW_PREFIX.test(url) ? `https://${url}` : url;
}

/**
 * Splits message text around absolute http(s) URLs and scheme-less `www.` URLs.
 * The `\b` keeps `www.` from matching mid-token (e.g. inside `subwww.foo`).
 *
 * Safe to share between calls: `String.prototype.split` does not carry
 * `lastIndex` the way `RegExp.prototype.test` does.
 */
export const MESSAGE_URL_SPLIT_REGEX = /((?:https?:\/\/|\bwww\.)[^\s]+)/gi;

/** Anchored, non-global counterpart of MESSAGE_URL_SPLIT_REGEX. */
const FULL_MESSAGE_URL_REGEX = /^(?:https?:\/\/|www\.)[^\s]+$/i;

/**
 * Reports whether a part produced by splitting on MESSAGE_URL_SPLIT_REGEX is
 * itself a URL, so it should be rendered as a link.
 *
 * This is a shape check only - deliberately looser than {@link isValidMessageURL},
 * matching how the renderers have always treated `https://` links. Uses an
 * anchored, non-global pattern so no `lastIndex` state leaks between calls.
 *
 * @param {string} part - A part returned by the split.
 * @returns {boolean} True when the part should render as a link.
 */
export function isMessageURLToken(part: string): boolean {
  return typeof part === 'string' && FULL_MESSAGE_URL_REGEX.test(part);
}

/**
 * Validates whether a given URL is valid.
 *
 * EC-44: reimplemented with the built-in `URL` parser instead of complex,
 * nested-quantifier regexes (which were vulnerable to ReDoS on adversarial
 * input). Parsing is linear-time and an explicit length cap bounds the work.
 *
 * A URL is considered valid when it parses, uses the http(s) scheme, and has a
 * dotted host whose final label (TLD) is at least two alphanumeric characters.
 *
 * An explicit scheme is required on purpose: EC-42 call sites use this as the
 * gate in front of `window.open`, and a scheme-less host would be resolved
 * against the CMS origin as a relative path. For message text, which may carry
 * a bare `www.` URL, use {@link isValidMessageURL} instead.
 *
 * @param {string} url - The URL string to validate.
 * @returns {boolean} True if the URL is a well-formed http(s) URL, false otherwise.
 */
export function isValidURL(url: string): boolean {
  if (typeof url !== 'string' || url.length === 0 || url.length > MAX_URL_LENGTH) {
    return false;
  }

  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }

  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    return false;
  }

  const labels = parsed.hostname.split('.');
  if (labels.length < 2) {
    return false;
  }

  const tld = labels[labels.length - 1];
  return /^[a-zA-Z0-9]{2,}$/.test(tld);
}

/**
 * Validates a URL token taken from message text.
 *
 * Same rules as {@link isValidURL}, except a bare `www.` URL is accepted and
 * treated as its `https://` equivalent - `www.google.com/` is as valid here as
 * `https://www.google.com/`, which is how both are rendered to recipients.
 *
 * @param {string} url - A URL token taken from message text.
 * @returns {boolean} True if the token is a linkable http(s) URL, false otherwise.
 */
export function isValidMessageURL(url: string): boolean {
  return isValidURL(normalizeMessageURL(url));
}

/**
 * Extracts potential URL tokens from the input text.
 * It splits the input string on whitespace and selects tokens that start with "htt" or contain "www".
 *
 * Splitting on all whitespace rather than just spaces matters: the prompt
 * composer is a textarea, so a link often sits at the end of a line. Splitting
 * on `' '` alone glued such a link to the neighbouring word, the combined token
 * failed to parse, and the send button was disabled for otherwise valid copy.
 *
 * @param {string} input - The input text from which to extract URLs.
 * @returns {string[]} An array of tokens that are potential URLs.
 */
export function extractURL(input: string) {
  const urls: string[] = [];

  for (let token of input.split(/\s+/)) {
    if (token.startsWith('htt') || token.includes('www')) {
      urls.push(token);
    }
  }

  return urls;
}

/**
 * Validates all URLs extracted from the input text.
 * Returns false if any of the extracted URLs is invalid; otherwise, returns true.
 *
 * @param {string} input - The input text containing one or more URLs.
 * @returns {boolean} True if all extracted URLs are valid, false otherwise.
 */
export function validateURLsFromInput(input: string) {
  const urls = extractURL(input);

  for (let url of urls) {
    if (!isValidMessageURL(url)) {
      return false;
    }
  }

  return true;
}
